import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120SubconsolService } from './f1120-subconsol.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AmountPipe } from '../../../DST/shared/pipes/amount.pipe';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-1120-subconsol',
    templateUrl: './f1120-subconsol.component.html',
    styleUrls: ['./f1120-subconsol.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120SubconsolComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  workSpaceData: any;

  rowData: any = {};
  filterParams: any = {};

  // Component Properties
  baseURLs: GtwElementsBaseUrlsModel = {} as GtwElementsBaseUrlsModel;
  globalService: any;
  GENERAL_CONFIG: any;
  
  userSettings: any = this.appService.USER_SETTINGS;
  logged_in_user: string = '';
  entity_type: string = '';
  
  // Life Insurance Properties
  li_loss_carryover: number = 0;
  nl_loss_carryover: number = 0;
  li_lpc_comments: string = "";
  nl_lpc_comments: string = "";
  li_jsonData: any[] = [];
  nl_jsonData: any[] = [];
  
  userreviewd: string = '';
  cmtflag: boolean = false;
  subconsole: boolean = false;
  system_updated: string = "System Calculated";
  title: string = "Override Limitation for Sub Consol";
  maindata: any[] = [];
  capitallossanalysis: any[] = [];
  
  // Loading and error states
  loading: boolean = false;
  minimise: boolean = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120SubconsolService: F1120SubconsolService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {}
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    const screenData = (activeScreen as any)?.data;
    this.workSpaceData = screenData?.data ?? screenData?.workspaceData ?? [];
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }
      
      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      this.userreviewd = this.userSettings?.user?.display_name || '';
      
      // 🔴 CRITICAL: Only proceed if we have row data - NO MOCKS!
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.entity_type = this.rowData.ENTITY_TYPE;
        this.processWorkspaceData();
        this.fetchConsolData();
      }
    } catch (e) {
      console.error('Error in F1120SubConsol init:', e);
    }
  }

  private processWorkspaceData() {
    // Process workspace data to extract carryover information
    // This replaces the workspaceFactory.activeScreen.data.data processing
    if (this.workSpaceData) {
      const workspaceData = this.workSpaceData;
      console.log('Workspace data:', workspaceData);
      for (let i = 0; i < workspaceData.length; i++) {
        if (workspaceData[i].LINE_TYPE === 'PUSH_CARRYOVER' && 
            workspaceData[i].ENTITY_TYPE === 'LI') {
          this.li_loss_carryover = workspaceData[i].AMOUNT;
          this.li_lpc_comments = "System Calculated based on Prior Year Life Insurance Amounts";
        }
        if (workspaceData[i].LINE_TYPE === 'PUSH_CARRYOVER' && 
            workspaceData[i].ENTITY_TYPE === 'REG') {
          this.nl_loss_carryover = workspaceData[i].AMOUNT;
          this.nl_lpc_comments = "System Calculated based on Prior Year Non-Life Amounts";
        }
      }
    }
  }

  // 🔴 CRITICAL: API calls use REAL rowData - NO globalParams!
  fetchConsolData() {
    this.loading = true;
    
    const params = {
      "action_code": 'em44az',
      "tax_year": this.rowData.TAX_YEAR,        // NOT globalParams.tax_year
      "scenario": this.rowData.SCENARIO,        // NOT globalParams.scenario  
      "jcd_key": this.rowData.JCD_KEY          // NOT globalParams.jcd_key
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.loading = false;
      
      if (data.callSuccess === "1") {
        this.li_jsonData = [];
        this.nl_jsonData = [];
        this.maindata = [];
        this.maindata = JSON.parse(JSON.stringify(data.jsonObject));
        if (this.maindata.length > 0) {
          // Match legacy behavior: index 0 → LI, index 1 → NL.
          // Sending all ENTITY_TYPE-filtered records caused ORA-01422 when
          // the API returned >2 rows (procedure SELECT INTO got multiple hits).
          this.li_jsonData.push(this.maindata[0]);
          if (this.maindata.length > 1) {
            this.nl_jsonData.push(this.maindata[1]);
          }
        } else {
          this.li_jsonData.push({ENTITY_TYPE: 'LI', AMOUNT: 0, COMMENTS: ''});
          this.nl_jsonData.push({ENTITY_TYPE: 'REG', AMOUNT: 0, COMMENTS: ''});
        }
       
      } else {
        this.handleFetchError(data);
      }
      this.cdr.markForCheck();
    });
  }

  private handleFetchError(data: any) {
    if (data.errorMessage === "no access") {
      this._snackBar.open(
        "Sorry you do not have access to do the requested action.",
        'Close',
        { duration: 4000, panelClass: ['error-snackbar'] }
      );
    } else {
      this._snackBar.open(
        "An error has occurred, If you continue to experience this issue please visit the help page to contact support.",
        'Close',
        { duration: 4000, panelClass: ['error-snackbar'] }
      );
    }
  }

  reset() {
    this.li_jsonData = [];
    this.nl_jsonData = [];
    this.fetchConsolData();
  }

  save() {
    if (this.subconsole) {
      this._snackBar.open("Please wait while we save this request", 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.subconsole = true;

    // Validate Life Insurance data
    if (this.li_jsonData.length > 0) {
      for (const item of this.li_jsonData) {
        if (item.AMOUNT === 0 || item.AMOUNT === undefined || 
            item.COMMENTS === "" || item.COMMENTS === undefined) {
          this._snackBar.open("Please correct the errors below", 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.subconsole = false;
          return;
        }
      }
    }

    // Validate Non-Life data
    if (this.nl_jsonData.length > 0) {
      for (const item of this.nl_jsonData) {
        if (item.AMOUNT === 0 || item.AMOUNT === undefined || 
            item.COMMENTS === "" || item.COMMENTS === undefined) {
          this._snackBar.open("Please correct the errors below", 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.subconsole = false;
          return;
        }
      }
    }

    // Match legacy: GlobalService.globalParams.tax_year / scenario / jcd_key
    const gp = this.globalService?.globalParams || {};
    const dummyJson = {
      "tax_year": String(gp.tax_year ?? this.rowData.TAX_YEAR),
      "scenario": String(gp.scenario ?? this.rowData.SCENARIO),
      "jcd_key": String(gp.jcd_key ?? this.rowData.JCD_KEY)
    };

    const sendobj = this.li_jsonData.concat(this.nl_jsonData);
    const message = "Saved Successfully!";

    this.reclassCRUDHttp(dummyJson, sendobj, message);
  }

  private reclassCRUDHttp(tempobj: any, accountArray: any[], message: string) {
    this.f1120SubconsolService.saveSubconsolData(tempobj, accountArray, this.globalService, this.GENERAL_CONFIG, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: any) => {
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.subconsole = false;
          this._snackBar.open(result.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.subconsole = false;
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          
          // Emit custom event for grid refresh
          const updateEvent = new CustomEvent('request-grid-update', {
            detail: 'refresh',
            bubbles: true
          });
          
          if (document.querySelector('gtw-1120-subconsol-ce')) {
            document.querySelector('gtw-1120-subconsol-ce')?.dispatchEvent(updateEvent);
          }
        }
        this.cdr.markForCheck();
      });
  }

  cancel() {
    // Emit custom event to close modal
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-1120-subconsol-ce')) {
      document.querySelector('gtw-1120-subconsol-ce')?.dispatchEvent(updateEvent);
    }
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item ? item.ENTITY_TYPE || index : index;
  }
}